// src/services/reports/reportService.ts

// Define a more specific type for items directly from Supabase before casting to TeamPlayerRawData
interface SupabaseTeamPlayerItem {
  status: string | null;
  teams: {
    name: string | null;
    sex: string | null;
  } | null;
}

import { supabase } from '@/lib/supabase';
import {
  TeamPlayerRawData,
  PlayerDispositionRow,
  PlayerDispositionTeamAssignment,
} from '@/types/reports';

interface PlayerDispositionRpcRow {
  player_id: string;
  first_name: string | null;
  last_name: string | null;
  sex: string | null;
  birthdate: string | null;
  age_group_name: string | null;
  age_group_sex: string | null;
  evaluations_attended: number | string | null;
  team_assignments: PlayerDispositionTeamAssignment[] | null;
}

export type PlayerDispositionGenderFilter = 'all' | 'male' | 'female';

function buildAgeGroupLabel(name: string | null, sex: string | null): string {
  if (!name) return 'Not Assigned';
  return `${sex === 'male' ? 'Boys' : 'Girls'} ${name}`;
}

export const fetchPlayerDispositions = async (
  genderFilter: PlayerDispositionGenderFilter
): Promise<PlayerDispositionRow[]> => {
  const { data, error } = await supabase.rpc('get_player_dispositions', {
    gender_filter: genderFilter === 'all' ? null : genderFilter,
  });

  if (error) {
    console.error('Error fetching player dispositions:', error);
    throw error;
  }

  const rows = (data ?? []) as PlayerDispositionRpcRow[];
  return rows.map((r) => ({
    player_id: r.player_id,
    first_name: r.first_name ?? '',
    last_name: r.last_name ?? '',
    sex: r.sex ?? '',
    birthdate: r.birthdate ?? null,
    age_group_label: buildAgeGroupLabel(r.age_group_name, r.age_group_sex),
    evaluations_attended: Number(r.evaluations_attended ?? 0),
    teams: Array.isArray(r.team_assignments) ? r.team_assignments : [],
  }));
};

/**
 * Fetches raw data for team player counts.
 * This function retrieves all player entries along with their associated team's name and sex.
 * The data will then be processed by a hook to group and count players.
 */
export const fetchTeamPlayerData = async (): Promise<TeamPlayerRawData[]> => {
  const { data, error } = await supabase
    .from('team_players')
    .select(`
      status,
      teams (
        name,
        sex
      )
    `);

  if (error) {
    console.error('Error fetching team player data:', error);
    throw error;
  }

  // Filter out any entries where the team relationship might be null if that's possible
  // and not desired for the report. For this query, if 'teams' is null, it implies
  // a team_player record without a valid team_id, which should be handled or logged.
  // Ensure that data from Supabase, even after filtering, aligns with TeamPlayerRawData[]
  // The filter now primarily ensures 'teams' is not null, further checks for array content happen in the hook.
  const typedData = data as unknown as SupabaseTeamPlayerItem[]; // Explicit cast via unknown
  return typedData.filter((item: SupabaseTeamPlayerItem): item is TeamPlayerRawData => 
    item.teams !== null
  ) as TeamPlayerRawData[];
};
